import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { SFArrowDropDownIcon, SFCloseIcon, SFSearchIcon } from "../../../icons";
import SFIconButton from "../../../button/iconButton";
import { SFStack } from "../../../grid";
import ActionBarSearchInputMenu from "./searchInputMenu";
import getStyled from "../../../../hooks/getStyled";

const { styled } = getStyled();

const TextFieldStyled = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "width",
})(({ theme, width }) => ({
  "& .MuiInputBase-root": {
    backgroundColor: theme.palette.grey100.main,
    borderRadius: "4px",
    borderColor: theme.palette.grey250.main,
    paddingLeft: "0px",
    paddingRight: "0px",
    width: width, // Use the dynamic width
    height: "34px",
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.grey250.main,
    },
    "&:hover": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.grey250.main,
      },
    },
    "&.Mui-focused": {
      ".MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${theme.palette.grey250.main}`,
      },
    },
  },
}));

const SearchInputWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "48px",
  padding: "8px 0px",
  paddingLeft: "15px",
  borderLeft: `1px solid ${theme.palette.grey100.main}`,
}));

const ToggleMenuButton = styled(SFIconButton, {
  shouldForwardProp: (prop) => prop !== "menuOpen",
})(({ theme, menuOpen }) => ({
  borderRadius: "4px 0px 0px 4px",
  backgroundColor: "#EEEEF6", // todo: not in theme, ask about this
  "&:hover": { backgroundColor: "#EEEEF6" }, // todo: not in theme, ask about this
  // Rotate the icon when the menu is toggled open/closed
  "& .MuiSvgIcon-root": {
    transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
  },
}));

const ClearButton = styled(SFIconButton)(() => ({
  borderRadius: "0px 4px 4px 0px",
  "&:hover": { backgroundColor: "transparent" },
}));

const ActionBarSearchInput = ({
  inputValue,
  onChange,
  onClear,
  dropdownMenuOpen,
  onDropdownMenuToggle,
  dropdownOptions = [],
  selectedDropdownOptions = [],
  onDropdownOptionClick,
  autoSize = false,
}) => {
  const { t } = useTranslation();
  const AUTO_COMPLETE_OFF = "off";
  const [inputWidth, setInputWidth] = useState("240px");
  const initialWidth = 200;
  const maxWidth = 470;
  const charWidth = 10;
  const threshold = initialWidth / charWidth;

  useEffect(() => {
    //Conditionally set the searchbar width
    if (autoSize) {
      if (inputValue.length > threshold) {
        // Calculate the width based on the length of the input value
        const newWidth = Math.min(
          initialWidth + (inputValue.length - threshold) * charWidth,
          maxWidth
        );
        setInputWidth(`${newWidth}px`);
      } else {
        setInputWidth(`${initialWidth}px`);
      }
    }
  }, [inputValue]);

  return (
    <>
      <SearchInputWrapper>
        <FormControl id="action-bar-search-input-form-control">
          <TextFieldStyled
            autoComplete={AUTO_COMPLETE_OFF}
            value={inputValue}
            onChange={onChange}
            id="action-bar-search-input"
            width={inputWidth} // Pass the dynamic width
            InputProps={{
              // Render the search icon and dropdown icon if dropdown is true
              startAdornment: (
                <InputAdornment position="start">
                  <SFStack direction="row" alignItems="center">
                    {dropdownOptions.length > 0 && (
                      <ToggleMenuButton
                        onClick={onDropdownMenuToggle}
                        menuOpen={dropdownMenuOpen}
                      >
                        <SFArrowDropDownIcon size="small" color="grey400" />
                      </ToggleMenuButton>
                    )}
                    <span style={{ width: "10px" }} />
                    <SFSearchIcon size="small" color="grey450" />
                  </SFStack>
                </InputAdornment>
              ),
              // Render the clear button when there is text in the input field
              endAdornment: inputValue && (
                <InputAdornment position="end" onClick={() => {}}>
                  <ClearButton onClick={onClear}>
                    <SFCloseIcon size="small" color="grey400" />
                  </ClearButton>
                </InputAdornment>
              ),
            }}
            variant="outlined"
            placeholder={autoSize ? t("components.actionBar.search.keywordPhrasePlaceholder"): t("components.actionBar.search.placeholder")}
          />
        </FormControl>
      </SearchInputWrapper>
      {dropdownOptions.length > 0 && (
        <ActionBarSearchInputMenu
          open={dropdownMenuOpen}
          options={dropdownOptions}
          selectedOptions={selectedDropdownOptions}
          onOptionClick={onDropdownOptionClick}
          anchorEl={document.getElementById(
            "action-bar-search-input-form-control"
          )}
        />
      )}
    </>
  );
};

export default ActionBarSearchInput;
